<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { api, ApiError } from '../api/client'
import PageBody from '../components/PageBody.vue'
import PageHeader from '../components/PageHeader.vue'
import { IconAlert, IconKey, IconTrash } from '../components/Icons'
import Modal from '../components/Modal.vue'
import { useToast } from '../lib/toast'
import { Badge, Button, EmptyState, ErrorState, Field, Input, Panel, SkeletonRows, Textarea } from '../components/ui'
import { expiryInfo, formatDateTime, fromLocalInput, relativeTime, shortID, toLocalInput } from '../lib/format'
import { useAsync, useTitle } from '../lib/hooks'

const PLAN_PRESETS = ['basic', 'standard', 'premium', 'trial', 'lifetime']
const PLAN_RE = /^[a-z0-9_-]{1,32}$/
const EXPIRY_TONE = { permanent: 'ok', active: 'ok', expiring: 'warn', expired: 'danger', none: 'neutral' } as const

const route = useRoute()
const router = useRouter()
const toast = useToast()
useTitle('账号详情')

const id = computed(() => String(route.params.id ?? ''))
const { data, error, reload } = useAsync(() => api.userDetail(id.value), () => [id.value])
const notFound = computed(() => error.value?.status === 404)

const plan = ref('')
const expires = ref('')
const note = ref('')
const formError = ref('')
const busy = ref(false)
const confirmRevoke = ref(false)

watch(
  () => data.value,
  (user) => {
    if (!user) return
    const current = user.entitlement
    plan.value = current?.plan ?? ''
    expires.value = current?.expires_at ? toLocalInput(current.expires_at) : ''
    note.value = current?.note ?? ''
    formError.value = ''
  },
  { immediate: true },
)

const current = computed(() => data.value?.entitlement ?? null)
const info = computed(() => (current.value ? expiryInfo(current.value.expires_at, current.value.active) : null))

async function submit() {
  const user = data.value
  if (!user) return
  const normalized = plan.value.trim().toLowerCase()
  if (!PLAN_RE.test(normalized)) {
    formError.value = '套餐标识只能用 1–32 位小写字母、数字、下划线或短横线'
    return
  }
  const iso = expires.value ? fromLocalInput(expires.value) : null
  if (iso && new Date(iso).getTime() <= Date.now()) {
    formError.value = '到期时间必须晚于当前时间；永久权益请留空'
    return
  }

  busy.value = true
  formError.value = ''
  try {
    await api.grantEntitlement(user.id, { plan: normalized, expires_at: iso, note: note.value.trim() })
    reload()
    toast.ok('权益已更新')
  } catch (err) {
    formError.value = err instanceof ApiError ? err.message : '保存失败'
  } finally {
    busy.value = false
  }
}

async function revoke() {
  const user = data.value
  if (!user) return
  busy.value = true
  try {
    await api.revokeEntitlement(user.id)
    confirmRevoke.value = false
    reload()
    toast.ok('权益已撤销')
  } catch (err) {
    toast.fail(err instanceof ApiError ? err.message : '撤销失败')
  } finally {
    busy.value = false
  }
}

function resetForm() {
  const c = current.value
  plan.value = c?.plan ?? ''
  expires.value = c?.expires_at ? toLocalInput(c.expires_at) : ''
  note.value = c?.note ?? ''
  formError.value = ''
}
</script>

<template>
  <template v-if="notFound">
    <PageHeader title="账号不存在" :breadcrumb="[{ label: '账号', to: '/users' }, { label: id }]" />
    <PageBody>
      <Panel>
        <EmptyState :title="`找不到账号 ${id}`" hint="账号可能已被删除。">
          <template #action>
            <Button size="sm" @click="router.push('/users')">返回账号列表</Button>
          </template>
        </EmptyState>
      </Panel>
    </PageBody>
  </template>

  <template v-else>
    <PageHeader
      :title="data?.email ?? id"
      :subtitle="data ? `注册于 ${formatDateTime(data.created_at)}` : undefined"
      :breadcrumb="[{ label: '账号', to: '/users' }, { label: data?.email ?? id }]"
    />
    <PageBody>
      <Panel v-if="error">
        <ErrorState :message="error.message" @retry="reload" />
      </Panel>
      <SkeletonRows v-else-if="!data" :rows="6" :cols="3" />
      <div v-else class="grid gap-4 xl:grid-cols-[320px_minmax(0,1fr)]">
        <Panel title="账号信息">
          <dl class="divide-y divide-line">
            <div class="flex items-start justify-between gap-4 px-4 py-2.5">
              <dt class="col-head pt-0.5">账号 ID</dt>
              <dd class="min-w-0 text-right text-[12.5px]">
                <span class="tnum" :title="data.id">{{ shortID(data.id, 10, 6) }}</span>
              </dd>
            </div>
            <div class="flex items-start justify-between gap-4 px-4 py-2.5">
              <dt class="col-head pt-0.5">邮箱</dt>
              <dd class="min-w-0 text-right text-[12.5px]"><span class="break-all">{{ data.email }}</span></dd>
            </div>
            <div class="flex items-start justify-between gap-4 px-4 py-2.5">
              <dt class="col-head pt-0.5">角色</dt>
              <dd class="min-w-0 text-right text-[12.5px]">
                <Badge :tone="data.role === 'admin' ? 'info' : 'neutral'">
                  {{ data.role === 'admin' ? '管理员' : '用户' }}
                </Badge>
              </dd>
            </div>
            <div class="flex items-start justify-between gap-4 px-4 py-2.5">
              <dt class="col-head pt-0.5">邮箱验证</dt>
              <dd class="min-w-0 text-right text-[12.5px]">
                <Badge v-if="data.verified_at" tone="ok">已验证</Badge>
                <Badge v-else tone="warn">未验证</Badge>
              </dd>
            </div>
            <div class="flex items-start justify-between gap-4 px-4 py-2.5">
              <dt class="col-head pt-0.5">活跃会话</dt>
              <dd class="min-w-0 text-right text-[12.5px]"><span class="tnum">{{ data.active_sessions }}</span></dd>
            </div>
            <div class="flex items-start justify-between gap-4 px-4 py-2.5">
              <dt class="col-head pt-0.5">注册时间</dt>
              <dd class="min-w-0 text-right text-[12.5px]">
                <span class="tnum">{{ formatDateTime(data.created_at) }}</span>
              </dd>
            </div>
          </dl>
          <p
            v-if="!data.verified_at"
            class="flex items-start gap-2 border-t border-line px-4 py-3 text-[12px] leading-relaxed text-muted"
          >
            <IconAlert :size="14" class="mt-0.5 shrink-0 text-warn" />
            邮箱未验证的账号即使激活权益，App 端登录也会被拒。
          </p>
        </Panel>

        <div class="flex flex-col gap-4">
          <Panel title="当前权益">
            <EmptyState
              v-if="!current"
              title="尚未激活权益"
              hint="该账号在 App 端无法播放任何内容。用下面的表单开通。"
            />
            <template v-else>
              <div class="flex flex-wrap items-center gap-3 border-b border-line px-4 py-3">
                <span class="tnum text-[20px] font-semibold">{{ current.plan }}</span>
                <Badge v-if="info" :tone="EXPIRY_TONE[info.state]">{{ info.label }}</Badge>
                <Badge v-if="current.status === 'revoked'" tone="danger">已撤销</Badge>
              </div>
              <dl class="divide-y divide-line">
                <div class="flex items-start justify-between gap-4 px-4 py-2.5">
                  <dt class="col-head pt-0.5">生效时间</dt>
                  <dd class="min-w-0 text-right text-[12.5px] break-words">{{ formatDateTime(current.starts_at) }}</dd>
                </div>
                <div class="flex items-start justify-between gap-4 px-4 py-2.5">
                  <dt class="col-head pt-0.5">到期时间</dt>
                  <dd class="min-w-0 text-right text-[12.5px] break-words">
                    {{ current.expires_at ? formatDateTime(current.expires_at) : '永久有效' }}
                  </dd>
                </div>
                <div class="flex items-start justify-between gap-4 px-4 py-2.5">
                  <dt class="col-head pt-0.5">最近更新</dt>
                  <dd class="min-w-0 text-right text-[12.5px] break-words">
                    {{ formatDateTime(current.updated_at) }}（{{ relativeTime(current.updated_at) }}）
                  </dd>
                </div>
                <div class="flex items-start justify-between gap-4 px-4 py-2.5">
                  <dt class="col-head pt-0.5">备注</dt>
                  <dd class="min-w-0 text-right text-[12.5px] break-words">{{ current.note || '—' }}</dd>
                </div>
              </dl>
            </template>
          </Panel>

          <Panel
            :title="current ? '调整权益' : '激活权益'"
            :hint="current ? '保存即覆盖当前权益' : '提交后立即生效'"
          >
            <template v-if="current && current.status === 'active'" #actions>
              <Button size="sm" variant="danger" @click="confirmRevoke = true">
                <IconTrash :size="13" />
                撤销
              </Button>
            </template>
            <div class="flex flex-col gap-4 px-4 py-4">
              <div class="grid gap-4 sm:grid-cols-2">
                <Field label="套餐标识" required hint="小写字母/数字/_-">
                  <Input v-model="plan" list="plan-presets" placeholder="premium" />
                </Field>
                <datalist id="plan-presets">
                  <option v-for="p in PLAN_PRESETS" :key="p" :value="p" />
                </datalist>
                <Field label="到期时间" hint="留空 = 永久有效">
                  <Input v-model="expires" type="datetime-local" />
                </Field>
              </div>
              <Field label="备注" hint="只给运营看，不影响下发内容">
                <Textarea v-model="note" :rows="2" placeholder="例如：2026-09 续费，渠道 A" />
              </Field>
              <p
                v-if="formError"
                role="alert"
                class="rounded-md border border-danger/40 bg-danger/10 px-3 py-2 text-[12.5px] text-danger"
              >
                {{ formError }}
              </p>
              <div class="flex items-center gap-2">
                <Button variant="primary" :loading="busy" @click="submit">
                  <IconKey :size="13" />
                  {{ current ? '保存权益' : '激活权益' }}
                </Button>
                <Button @click="resetForm">重置</Button>
                <RouterLink to="/users" class="ml-auto text-[12.5px] text-link hover:underline">返回列表</RouterLink>
              </div>
            </div>
          </Panel>

          <Modal
            :open="confirmRevoke"
            title="撤销该账号的权益？"
            hint="账号会立即无法播放内容，权益记录保留（状态改为已撤销）。"
            @close="confirmRevoke = false"
          >
            <template #footer>
              <Button @click="confirmRevoke = false">取消</Button>
              <Button variant="danger" :loading="busy" @click="revoke">确认撤销</Button>
            </template>
            <p class="text-[13px] leading-relaxed text-muted">
              撤销 <span class="text-text">{{ data.email }}</span> 的
              <span class="tnum text-text">{{ current?.plan }}</span> 权益。 之后可以随时用同一表单重新激活。
            </p>
          </Modal>
        </div>
      </div>
    </PageBody>
  </template>
</template>
